
import { Link } from "react-router-dom";
import { Heart, ArrowDown, Book, Stethoscope, Brain, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import Header from "@/components/shared/Header";

const HealthSciences = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero Section */}
      <section className="py-20 px-6 bg-whiter from-primary/5 to-muted/20">
        <div className="container mx-auto text-center max-w-4xl">
          <div className="mx-auto w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-8">
            <Heart className="w-10 h-10 text-primary" />
          </div>
          <h1 className="text-5xl font-bold mb-6 bg-white from-primary to-primary/70 bg-clip-text text-transparent">
            Health Sciences
          </h1>
          <p className="text-xl text-black leading-relaxed mb-8">
            The comprehensive study and practice of human health—from molecular biology to systems of care, 
            from preventative models to psychological and therapeutic interventions.
          </p>
          <ArrowDown className="mx-auto w-6 h-6 text-black animate-bounce" />
        </div>
      </section>

      {/* Disciplines */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid gap-8">
            
            {/* Core Medical Sciences */}
            <Card className="group hover:shadow-lg transition-all duration-300">
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <Stethoscope className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl">Core Medical Sciences</CardTitle>
                    <CardDescription className="text-lg">
                      The biological and physiological foundation of modern healthcare practice
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-semibold mb-3 text-primary">Basic Sciences</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Anatomy and Physiology</li>
                      <li>• Biochemistry and Molecular Biology</li>
                      <li>• Pathology and Microbiology</li>
                      <li>• Pharmacology</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 text-primary">Clinical Practice</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Internal Medicine</li>
                      <li>• Surgery and Emergency Medicine</li>
                      <li>• Pediatrics and Geriatrics</li>
                      <li>• Medical Diagnostics</li>
                    </ul>
                  </div>
                </div>
                <Button asChild>
                  <Link to="/health-sciences/core-medical-sciences">Explore Core Medical Sciences</Link>
                </Button>
              </CardContent>
            </Card>

            {/* Allied Health and Clinical Practice */}
            <Card className="group hover:shadow-lg transition-all duration-300">
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <Book className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl">Allied Health & Clinical Practice</CardTitle>
                    <CardDescription className="text-lg">
                      Specialized disciplines that support and extend primary medical care
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-semibold mb-3 text-primary">Therapeutic Services</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Nursing Science</li>
                      <li>• Physiotherapy and Occupational Therapy</li>
                      <li>• Rehabilitation Medicine</li>
                      <li>• Pain Management</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 text-primary">Diagnostic Services</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Medical Imaging and Radiologic Sciences</li>
                      <li>• Laboratory Medicine</li>
                      <li>• Biomedical Technology</li>
                      <li>• Clinical Biochemistry</li>
                    </ul>
                  </div>
                </div>
                <Button asChild>
                  <Link to="/health-sciences/allied-health">Explore Allied Health</Link>
                </Button>
              </CardContent>
            </Card>

            {/* Mental and Behavioral Health */}
            <Card className="group hover:shadow-lg transition-all duration-300">
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <Brain className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl">Mental & Behavioral Health</CardTitle>
                    <CardDescription className="text-lg">
                      The psychological, neurological, and behavioral dimensions of human health
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-semibold mb-3 text-primary">Clinical Psychology</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Mental Health Disorders</li>
                      <li>• Cognitive-Behavioral Therapy</li>
                      <li>• Psychometric Assessments</li>
                      <li>• Trauma-Informed Care</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 text-primary">Neuroscience</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Psychiatry and Psychopharmacology</li>
                      <li>• Neuropsychology</li>
                      <li>• Brain Imaging and Cognition</li>
                      <li>• Behavioral Therapy</li>
                    </ul>
                  </div>
                </div>
                <Button asChild>
                  <Link to="/health-sciences/mental-behavioral-health">Explore Mental Health</Link>
                </Button>
              </CardContent>
            </Card>

            {/* Public and Global Health */}
            <Card className="group hover:shadow-lg transition-all duration-300">
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <Globe className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl">Public & Global Health</CardTitle>
                    <CardDescription className="text-lg">
                      Population-level health, preventive strategies, and the global burden of disease
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-semibold mb-3 text-primary">Population Health</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Epidemiology and Biostatistics</li>
                      <li>• Health Policy and Management</li>
                      <li>• Global Health Organizations</li>
                      <li>• Disease Prevention</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 text-primary">Environmental Health</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Environmental and Occupational Health</li>
                      <li>• Nutrition and Preventive Medicine</li>
                      <li>• Climate Change and Health</li>
                      <li>• One Health Approaches</li>
                    </ul>
                  </div>
                </div>
                <Button asChild>
                  <Link to="/health-sciences/public-global-health">Explore Public Health</Link>
                </Button>
              </CardContent>
            </Card>

          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-16 px-6 bg-muted/30">
        <div className="container mx-auto text-center max-w-4xl">
          <h3 className="text-2xl font-bold mb-6">Continue Your Journey</h3>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="outline">
              <Link to="/world-itself">Back to The World Itself</Link>
            </Button>
            <Button asChild size="lg">
              <Link to="/convergence">Discover Intersections</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HealthSciences;

